import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  InputSignal,
  Signal,
  WritableSignal,
  afterRenderEffect,
  computed,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatTooltip } from '@angular/material/tooltip';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

import { CoreInsightsEvent, InsightsContextAware, InsightsEventProperties } from '@core/shared/util';

import { NavigationItemComponent } from '../navigation-item/navigation-item.component';

export interface NavigationItem {
  label: string;
  icon: string;
  route: string;
}

export interface NavigationGroup extends NavigationItem {
  children?: NavigationItem[];
}

@Component({
  selector: 'mp-navigation-group',
  templateUrl: './navigation-group.component.html',
  styleUrl: './navigation-group.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': 'class',
    '(mouseenter)': 'showGroupSubmenu()',
    '(mouseleave)': 'hideGroupSubmenu()',
  },
  imports: [MatTooltip, NavigationItemComponent],
})
export class NavigationGroupComponent extends InsightsContextAware {
  private readonly router: Router = inject(Router);

  protected readonly class = 'mp-navigation-group';

  readonly navigationGroup: InputSignal<NavigationGroup> = input.required<NavigationGroup>();

  readonly isExpanded: InputSignal<boolean> = input<boolean>(false);

  readonly sideNavRef: InputSignal<ElementRef<HTMLElement> | undefined> = input<ElementRef<HTMLElement>>();

  protected readonly children: Signal<NavigationItem[]> = computed(() => this.navigationGroup().children || []);

  // Aktualisiert sich nach jeder abgeschlossenen Navigation -> isActive ist unter OnPush reaktiv
  // (sonst aktualisiert sich der Aktiv-/Aufgeklappt-Zustand erst beim ersten Hover).
  private readonly routerNavigation = toSignal(
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)),
  );

  protected readonly isActive: Signal<boolean> = computed(() => {
    this.routerNavigation();

    return this.router.isActive(this.navigationGroup().route, {
      paths: 'subset',
      queryParams: 'ignored',
      fragment: 'ignored',
      matrixParams: 'ignored',
    });
  });

  protected readonly tooltipMessage: Signal<string> = computed(() => {
    if (this.children().length || this.isExpanded()) {
      return '';
    }

    return this.navigationGroup().label;
  });

  protected readonly isSubmenuVisible: WritableSignal<boolean> = signal<boolean>(false);

  protected readonly linkRef: Signal<ElementRef<HTMLElement>> = viewChild.required('link', {
    read: ElementRef<HTMLElement>,
  });

  protected readonly groupContentRef: Signal<ElementRef<HTMLDivElement> | undefined> =
    viewChild<ElementRef<HTMLDivElement>>('groupcontent');

  constructor() {
    super();

    afterRenderEffect(() => {
      if (this.children().length && this.isSubmenuVisible()) {
        this.handleSubMenuContainByScreen();
      }
    });
  }

  trackItemClick(label: string): void {
    this.trackInsightsEvent(CoreInsightsEvent.NAVIGATION_ITEM_CLICK, {
      group: label,
    });
  }

  trackChildItemClick(label: string, childItemLabel: string): void {
    this.trackInsightsEvent(CoreInsightsEvent.NAVIGATION_ITEM_CLICK, {
      group: label,
      value: childItemLabel,
    });
  }

  protected showGroupSubmenu(): void {
    this.isSubmenuVisible.set(true);
  }

  protected hideGroupSubmenu(): void {
    this.isSubmenuVisible.set(false);
  }

  private handleSubMenuContainByScreen(): void {
    const link: HTMLElement = this.linkRef().nativeElement;
    const group: HTMLDivElement | undefined = this.groupContentRef()?.nativeElement;
    const sideNav: HTMLElement | undefined = this.sideNavRef()?.nativeElement;

    if (!group || !sideNav) {
      return;
    }

    if (this.isExpanded()) {
      group.style.top = group.style.left = '';
      return;
    }

    const linkRect = link.getBoundingClientRect();
    const groupRect = group.getBoundingClientRect();
    const sideNavRect = sideNav.getBoundingClientRect();
    const buffer = 10;

    const topRelativeToScreenEdge =
      linkRect.top + groupRect.height + buffer < window.innerHeight
        ? linkRect.top
        : window.innerHeight - groupRect.height - buffer;

    // NOTE: Even though submenu is fixed it's still positioned relatively to the sidenav as it applies some 'transform' css style
    const topRelativeToSidenav = topRelativeToScreenEdge - sideNavRect.top;

    group.style.top = `${Math.ceil(topRelativeToSidenav)}px`;
    group.style.left = `${Math.ceil(linkRect.right)}px`;
  }

  override trackInsightsEvent(event: CoreInsightsEvent, customProperties: InsightsEventProperties): void {
    super.trackInsightsEvent(event, customProperties, { includePageName: false });
  }
}
